<script setup lang="ts">
/**
 * 自定义设置's pictures: a console window in miniature, as the clouds'
 * settings drawers draw theirs — the bar with its three dots, the menu's
 * lines, a page of rows — in a theme's colours (浅色, 深色, 双色) and with
 * the parts a layout choice shows or hides. The part a choice is about
 * (focus) is drawn in the skin's colour. On the primary's lightest tint,
 * so it follows the skin and the console's own dark mode.
 */
import { computed } from 'vue'
import type { Theme } from '@/utils/layout'

const props = withDefaults(
  defineProps<{
    tone?: Theme
    navbar?: boolean
    menu?: boolean
    title?: boolean
    focus?: 'navbar' | 'menu' | 'title'
  }>(),
  { tone: 'light', navbar: true, menu: true, title: true, focus: undefined },
)

const LIGHT = { surface: '#ffffff', rule: '#eceef3', line: '#e3e5ea', dot: '#c9cdd4', strong: '#c2c7d0' }
const DARK = { surface: '#2c2c2c', rule: '#383838', line: '#474747', dot: '#666666', strong: '#5c5c5c' }
// The bar and menu's colours, and the page's.
const chrome = computed(() => (props.tone === 'light' ? LIGHT : DARK))
const page = computed(() => (props.tone === 'dark' ? DARK : LIGHT))

// The window, flush with the picture's foot as the clouds' are.
const W = { x: 18, y: 18, w: 156, h: 90 }
const BAR = 12
const MENU = 40
const top = computed(() => W.y + (props.navbar ? BAR : 0))
const left = computed(() => W.x + (props.menu ? MENU : 0))
const focusColor = 'rgb(var(--primary-6))'
const tint = (on: boolean, c: string) => (on ? focusColor : c)
</script>

<template>
  <svg class="thumb" viewBox="0 0 192 108" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="192" height="108" class="ground" />
    <!-- The page. -->
    <rect :x="W.x" :y="W.y" :width="W.w" :height="W.h" :fill="page.surface" />
    <!-- The bar: its dots, a rule under it. -->
    <template v-if="navbar">
      <rect :x="W.x" :y="W.y" :width="W.w" :height="BAR" :fill="chrome.surface" />
      <rect :x="W.x" :y="W.y + BAR - 1" :width="W.w" height="1" :fill="tint(focus === 'navbar', chrome.rule)" />
      <circle v-for="i in 3" :key="i" :cx="W.x + 6 + (i - 1) * 7" :cy="W.y + 6" r="2" :fill="tint(focus === 'navbar', chrome.dot)" />
    </template>
    <!-- The menu: a brand line, then its entries. -->
    <template v-if="menu">
      <rect :x="W.x" :y="top" :width="MENU" :height="W.y + W.h - top" :fill="chrome.surface" />
      <rect :x="W.x + MENU - 1" :y="top" width="1" :height="W.y + W.h - top" :fill="tint(focus === 'menu', chrome.rule)" />
      <circle :cx="W.x + 8" :cy="top + 9" r="3" :fill="tint(focus === 'menu', chrome.strong)" />
      <rect :x="W.x + 14" :y="top + 8" width="18" height="2" :fill="tint(focus === 'menu', chrome.strong)" />
      <rect v-for="i in 5" :key="i" :x="W.x + 6" :y="top + 16 + i * 8" :width="i % 2 ? 26 : 20" height="2" :fill="tint(focus === 'menu', chrome.line)" />
    </template>
    <!-- The page: its heading, then rows of a list. -->
    <template v-if="title">
      <circle :cx="left + 10" :cy="top + 9" r="3" :fill="tint(focus === 'title', page.strong)" />
      <rect :x="left + 16" :y="top + 7" width="22" height="2" :fill="tint(focus === 'title', page.strong)" />
      <rect :x="left + 16" :y="top + 11" width="14" height="2" :fill="tint(focus === 'title', page.line)" />
    </template>
    <template v-for="i in 5" :key="`r${i}`">
      <rect :x="left + 8" :y="top + (title ? 22 : 10) + (i - 1) * 9" width="4" height="2" :fill="page.line" />
      <rect :x="left + 18" :y="top + (title ? 22 : 10) + (i - 1) * 9" :width="i % 2 ? 72 : 56" height="2" :fill="page.line" />
    </template>
  </svg>
</template>

<style scoped>
.thumb {
  display: block;
  width: 100%;
  height: auto;
}
.ground {
  fill: rgb(var(--primary-1));
}
</style>
